import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import MkdSDK from "@/utils/MkdSDK";
import { GlobalContext, showToast } from "@/context/Global";
import { AuthContext, tokenExpireError } from "@/context/Auth";
import { LazyLoad } from "@/components/LazyLoad";
import { MkdInput } from "@/components/MkdInput";
import { InteractiveButton } from "@/components/InteractiveButton";
import { ArrowLeft } from "lucide-react";

interface PriceFormData {
  name: string;
  status: string | number;
}

const EditAdminPricePage: React.FC = () => {
  const { dispatch: globalDispatch } = React.useContext(GlobalContext);
  const { dispatch: authDispatch } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const params = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [priceData, setPriceData] = useState<any>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PriceFormData>();

  // Fetch price data
  useEffect(() => {
    const fetchPriceData = async () => {
      setLoading(true);
      try {
        const sdk = new MkdSDK();
        const result = await sdk.callRawAPI(
          `/v1/api/contentgap/customer/lambda/stripe/price/${params.id}`,
          {},
          "GET"
        );

        if (!result.error) {
          setPriceData(result.price);
          // Set form default values
          reset({
            name: result.model.name,
            status: result.model.active ? "active" : "inactive",
          });
        } else {
          showToast(globalDispatch, "Failed to load price data", 5000, "error");
          navigate("/admin/prices");
        }
      } catch (error: any) {
        console.error("Error fetching price:", error);
        tokenExpireError(authDispatch, error.message);
      } finally {
        setLoading(false);
      }
    };

    if (params.id) {
      fetchPriceData();
    }
  }, [params.id, globalDispatch, authDispatch, navigate, reset]);

  const onSubmit = async (data: PriceFormData) => {
    setIsSubmitting(true);
    try {
      const sdk = new MkdSDK();

      // Prepare update data
      const updateData = {
        name: data.name,
        status: data.status === "active" ? true : false,
      };

      // Update the price
      const result = await sdk.updateStripePrice(
        params.id as string,
        updateData
      );

      if (!result.error) {
        showToast(
          globalDispatch,
          "Price updated successfully",
          3000,
          "success"
        );
        navigate(`/admin/prices/view/${params.id}`);
      } else {
        showToast(
          globalDispatch,
          result.message || "Failed to update price",
          3000,
          "error"
        );
      }
    } catch (error: any) {
      console.error("Error updating price:", error);
      showToast(
        globalDispatch,
        error.message || "An error occurred",
        3000,
        "error"
      );
      tokenExpireError(authDispatch, error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-t-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto p-6">
      <div className="mb-6 flex items-center">
        <InteractiveButton
          onClick={() => navigate(`/admin/prices/view/${params.id}`)}
          className="mr-4 !bg-transparent !p-2 !text-gray-600 hover:!bg-gray-100"
        >
          <ArrowLeft size={20} />
        </InteractiveButton>
        <h1 className="text-2xl font-bold text-gray-800">Edit Price</h1>
      </div>

      <div className="max-w-2xl rounded-lg bg-white p-6 shadow-md">
        {priceData && (
          <div className="mb-6">
            <div className="mb-2 text-sm font-medium text-gray-500">
              Price Details (Read-only)
            </div>
            <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 md:grid-cols-2">
              <div>
                <span className="text-xs font-medium text-gray-500">
                  Amount:
                </span>
                <p className="text-sm font-semibold">
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: priceData.currency || "USD",
                  }).format(priceData.amount / 100)}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">Type:</span>
                <p className="text-sm font-semibold capitalize">
                  {priceData.type}
                </p>
              </div>
              {priceData.type === "recurring" && (
                <>
                  <div>
                    <span className="text-xs font-medium text-gray-500">
                      Interval:
                    </span>
                    <p className="text-sm font-semibold capitalize">
                      {priceData.interval_count > 1
                        ? `${priceData.interval_count} ${priceData.interval}s`
                        : priceData.interval}
                    </p>
                  </div>
                </>
              )}
              <div>
                <span className="text-xs font-medium text-gray-500">
                  Trial Days:
                </span>
                <p className="text-sm font-semibold">
                  {priceData.trial_days || 0}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">
                  Usage Metered:
                </span>
                <p className="text-sm font-semibold">
                  {priceData.is_usage_metered ? "Yes" : "No"}
                </p>
              </div>
              {priceData.is_usage_metered && (
                <div>
                  <span className="text-xs font-medium text-gray-500">
                    Usage Limit:
                  </span>
                  <p className="text-sm font-semibold">
                    {priceData.usage_limit || "Unlimited"}
                  </p>
                </div>
              )}
              <div>
                <span className="text-xs font-medium text-gray-500">
                  Pay Per Usage:
                </span>
                <p className="text-sm font-semibold">
                  {priceData.pay_per_usage ? "Yes" : "No"}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500">
                  Pay Per Seat:
                </span>
                <p className="text-sm font-semibold">
                  {priceData.pay_per_seat ? "Yes" : "No"}
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Price Name */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="text"
                  name="name"
                  label="Price Name"
                  register={register}
                  errors={errors}
                  required={true}
                />
              </LazyLoad>
            </div>

            {/* Status */}
            <div>
              <LazyLoad>
                <MkdInput
                  type="select"
                  name="status"
                  label="Status"
                  register={register}
                  errors={errors}
                  options={[
                    { value: "active", label: "Active" },
                    { value: "inactive", label: "Inactive" },
                  ]}
                  required={true}
                />
              </LazyLoad>
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <InteractiveButton
              onClick={() => navigate(`/admin/prices/view/${params.id}`)}
              className="!bg-gray-200 !text-gray-800 hover:!bg-gray-300"
            >
              Cancel
            </InteractiveButton>
            <InteractiveButton
              type="submit"
              disabled={isSubmitting}
              className="!bg-blue-600 hover:!bg-blue-700"
            >
              {isSubmitting ? "Updating..." : "Update Price"}
            </InteractiveButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdminPricePage;
